import _ from "lodash"
import { useMemo } from "react"

import {
  DropdownItemType,
  ExtendedMetricSelectorProps,
} from "@costory/types/endpoints/DropdownTypes"
import { MetricsResponses } from "@costory/types/endpoints/metrics"
import { Currency } from "@costory/types/prisma-client"

import { IconMetrics } from "@costory/front/components/IconMetrics"
import BaseDropdownSelector from "@costory/front/components/dropdown/BaseDropdownSelector"
import RightPanel from "@costory/front/components/dropdown/RightPanel"
import { COST_METRIC_DESCRIPTIONS } from "@costory/front/components/dropdown/constants"

const MetricSelector = ({
  metric: selectedMetric,
  setMetric,
  metricsOptions,
  setCurrency = undefined,
  onlyCostGroup = false,
  hideGroupPills = false,
  label = "Metric",
  dropdownHeight = 300,
}: ExtendedMetricSelectorProps) => {
  const groupedMetrics = useMemo(() => {
    const { true: costMetrics = [], false: otherMetrics = [] } = _.groupBy(
      metricsOptions,
      "technical",
    )
    const advancedCostMetrics = costMetrics.filter(
      (metric) => metric.group == MetricsResponses.CostCategory.AdvancedCost,
    )
    const basicCostMetrics = costMetrics.filter(
      (metric) => metric.group == MetricsResponses.CostCategory.Cost,
    )
    if (onlyCostGroup) {
      return [
        { group: "Cost", items: basicCostMetrics },
        ...(advancedCostMetrics.length > 0
          ? [{ group: "Advanced Cost", items: advancedCostMetrics }]
          : []),
      ]
    }
    return [
      { group: "Cost", items: basicCostMetrics },
      ...(advancedCostMetrics.length > 0
        ? [{ group: "Advanced Cost", items: advancedCostMetrics }]
        : []),
      { group: "Formula", items: otherMetrics },
    ]
  }, [metricsOptions, onlyCostGroup])

  // Right panel renderer for metric selector
  const renderRightPanel = (selectedItem?: DropdownItemType) => {
    if (!selectedItem) return null

    const description = COST_METRIC_DESCRIPTIONS[selectedItem.value] || ""
    const formula = selectedItem.formula
    const datasource = selectedItem.datasource
    const rawMetric = selectedItem.rawMetric

    return (
      <RightPanel
        item={selectedItem}
        description={description}
        formula={formula}
        datasource={datasource}
        rawMetric={rawMetric}
        noDataMessage="No description available"
      />
    )
  }

  const getIcon = (value: string) => <IconMetrics value={value} />
  const handleChange = (value: string) => {
    const metric = metricsOptions.find((metric) => metric.id === value)
    if (metric?.onlyUSD && setCurrency) {
      setCurrency(Currency.USD)
    }
    setMetric(value)
  }
  return (
    <BaseDropdownSelector
      value={selectedMetric}
      onChange={handleChange}
      options={groupedMetrics}
      label={label}
      placeholder="Select metric"
      width={500}
      dropdownHeight={dropdownHeight}
      inputWidth={200}
      showGroupPills={!hideGroupPills}
      showAllPill={!onlyCostGroup}
      getIcon={getIcon}
      renderRightPanel={renderRightPanel}
    />
  )
}

export default MetricSelector
